/**
 * Player-facing copy for the location gate.
 *
 * Two messages, not one, and the distinction is the point. A player refused
 * because they are genuinely outside an approved region needs to be told that.
 * A player refused because our own check could not complete must NOT be, because
 * they may well be perfectly allowed and telling them otherwise is both wrong
 * and a support ticket.
 *
 * Ported from enhancement/EN-9657_SupportForGeoChanges, which split these for
 * the same reason.
 */

/** Genuinely outside an approved region. The verdict was reached and it was no. */
export const REGION_RESTRICTED_MESSAGE =
  'You appear to be outside our supported region.\nHijack Poker is currently only available in Texas.\nFor assistance, please reach out to support@hijackpoker.com.';

/**
 * We could not reach a verdict. Deliberately says nothing about the player's
 * location, because we do not know it: this covers a failed lookup, an
 * unreachable endpoint, and the server failing closed on an internal error.
 */
export const SERVER_TROUBLE_MESSAGE =
  'We are having trouble confirming your location right now. Please try again in a moment.';

/**
 * The server `reason` values that mean a genuine regional refusal.
 *
 * An ALLOWLIST, not a denylist. This was previously inverted — a set of
 * "inconclusive" reasons, with everything else falling through to the regional
 * message — which meant any reason the client did not recognise (newly added
 * server-side, misspelled, or malformed) told the player they were out of
 * region when we had established no such thing. The docblock above claimed the
 * safe behaviour while the code did the unsafe one.
 *
 * With an allowlist, an unrecognised reason degrades to the vague message,
 * which is the failure we can live with.
 */
const REGIONAL_REFUSAL_REASONS = new Set(['not_allowed_region']);

/**
 * Choose the copy for a refusal.
 *
 * Defaults to the region message only for reasons we recognise as genuine
 * refusals; anything unrecognised is treated as inconclusive. That asymmetry is
 * deliberate: wrongly telling an allowed player they are out of region is a
 * worse failure than being vague to a player who really is.
 */
export function messageForRefusal(reason: string | undefined): string {
  return REGIONAL_REFUSAL_REASONS.has(reason ?? '')
    ? REGION_RESTRICTED_MESSAGE
    : SERVER_TROUBLE_MESSAGE;
}
